import React, { useMemo, useState } from "react";
import { Copy, Check, ExternalLink, Code2, Palette } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin panel that shows the embed code + a live preview.
 * Rendered inline on the Leads page.
 */
export default function EmbedCodePanel() {
  const origin = window.location.origin;
  const [theme, setTheme] = useState("light");
  const [accent, setAccent] = useState("#0F172A");
  const [tab, setTab] = useState("iframe"); // iframe | html
  const [copied, setCopied] = useState("");

  const embedUrl = useMemo(() => {
    const p = new URLSearchParams({ theme, accent, source: "shopify" });
    return `${origin}/embed/lead-form?${p.toString()}`;
  }, [origin, theme, accent]);

  const iframeCode = `<iframe
  id="wrapconsole-lead-form-iframe"
  src="${embedUrl}"
  title="Quote request form"
  style="width:100%; min-height:640px; border:0; border-radius:16px; display:block;"
  loading="lazy">
</iframe>
<script>
(function () {
  // Auto-resize the iframe to match the form's real content height, so the
  // customer never sees an inner scrollbar or a form clipped at the bottom.
  window.addEventListener('message', function (ev) {
    var data = ev && ev.data;
    if (!data || data.type !== 'wrapconsole-resize') return;
    var f = document.getElementById('wrapconsole-lead-form-iframe');
    if (f && typeof data.height === 'number') {
      f.style.height = (data.height + 4) + 'px';
    }
  });
})();
</script>`;

  const scriptCode = `<div id="wrapconsole-lead-form" style="min-height:640px;"></div>
<script>
(function(){
  var el = document.getElementById('wrapconsole-lead-form');
  if (!el) return;
  var f = document.createElement('iframe');
  f.src = '${embedUrl}';
  f.title = 'Quote request form';
  f.style.cssText = 'width:100%;min-height:640px;border:0;border-radius:16px;display:block;';
  f.loading = 'lazy';
  el.innerHTML = '';
  el.appendChild(f);
  // Auto-resize on every content change reported by the embed page.
  window.addEventListener('message', function (ev) {
    var data = ev && ev.data;
    if (!data || data.type !== 'wrapconsole-resize') return;
    if (typeof data.height === 'number') {
      f.style.height = (data.height + 4) + 'px';
    }
  });
})();
</script>`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied");
      setTimeout(() => setCopied(""), 2000);
    } catch {
      toast.error("Copy blocked — select the code manually");
    }
  };

  return (
    <div data-testid="embed-code-panel" className="glass rounded-3xl p-5 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-label flex items-center gap-2"><Code2 className="w-3 h-3" /> Shopify / Website Embed</div>
          <h3 className="font-heading text-xl text-white mt-1">Drop-in lead capture form</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-md">
            Paste the code below into a Shopify custom-HTML section (or any website).
            Submissions appear here as new leads with source=<code className="text-slate-300">shopify</code>.
          </p>
        </div>
        <a
          href={embedUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="embed-open-preview"
          className="text-xs flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-slate-200"
        >
          <ExternalLink className="w-3 h-3" /> Open preview
        </a>
      </div>

      {/* Customization */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white/[0.02] border border-white/10 rounded-2xl p-4">
        <div>
          <div className="text-label mb-1.5 flex items-center gap-1"><Palette className="w-3 h-3" /> Theme</div>
          <div className="flex gap-1">
            {["light", "dark"].map((t) => (
              <button
                key={t}
                data-testid={`embed-theme-${t}`}
                onClick={() => setTheme(t)}
                className={`flex-1 text-xs py-1.5 rounded-full border transition ${theme === t ? "bg-white text-black border-white" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-label mb-1.5">Accent color</div>
          <div className="flex items-center gap-2">
            <input
              data-testid="embed-accent-color"
              type="color"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              className="w-9 h-9 rounded-lg border border-white/10 bg-transparent cursor-pointer"
            />
            <input
              type="text"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white font-mono"
            />
          </div>
        </div>
        <div>
          <div className="text-label mb-1.5">Embed URL</div>
          <button
            onClick={() => copy(embedUrl, "url")}
            data-testid="embed-copy-url"
            className="w-full text-xs flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-2 py-1.5 text-slate-300"
          >
            {copied === "url" ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
            <span className="truncate">{embedUrl.replace(origin, "").slice(0, 40)}…</span>
          </button>
        </div>
      </div>

      {/* Code block tabs */}
      <div>
        <div className="flex gap-1 mb-2">
          {[
            { id: "iframe", label: "Iframe (Shopify HTML block)" },
            { id: "html", label: "Script snippet" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`embed-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${tab === t.id ? "bg-white text-black border-white" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="relative">
          <pre
            data-testid="embed-code-block"
            className="bg-black/60 border border-white/10 rounded-2xl p-4 pr-14 text-xs text-slate-300 font-mono overflow-x-auto whitespace-pre max-h-72"
          >
            {tab === "iframe" ? iframeCode : scriptCode}
          </pre>
          <button
            onClick={() => copy(tab === "iframe" ? iframeCode : scriptCode, tab)}
            data-testid="embed-copy-code"
            className="absolute top-3 right-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"
          >
            {copied === tab ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* How-to */}
      <details className="text-xs text-slate-400 bg-white/[0.02] border border-white/10 rounded-2xl p-4">
        <summary className="cursor-pointer text-slate-200 font-medium">How to add this to Shopify</summary>
        <ol className="list-decimal list-inside mt-2 space-y-1 marker:text-slate-500">
          <li>In Shopify admin, go to <span className="text-slate-200">Online Store → Pages</span> (or edit an existing page).</li>
          <li>Click the <span className="text-slate-200">&lt;/&gt;</span> button in the rich-text editor to switch to HTML source view.</li>
          <li>Paste the iframe code above where you want the form to appear.</li>
          <li>Save. The form will appear on your public page and submissions will land in your Leads tab here.</li>
        </ol>
      </details>
    </div>
  );
}
